import type { AppRouterOutputs } from '@alison/api/router';
import EditOutlined from '@mui/icons-material/EditOutlined';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useRenameRun } from '../../../lib/automations.hooks.js';

const MAX_LABEL = 120;

export type AutomationRef = NonNullable<
  NonNullable<AppRouterOutputs['workflows']['getSession']>['automation']
>;

// The run's own name, or its automation's when an older occurrence has none.
export function runLabelOf(automation: AutomationRef): string {
  return automation.label ?? automation.name;
}

export function RunLabel({ automation }: { automation: AutomationRef }): ReactNode {
  const rename = useRenameRun();
  const [draft, setDraft] = useState<string | null>(null);

  const handleSave = (): void => {
    const label = draft?.trim() ?? '';
    if (label === '' || label === runLabelOf(automation)) {
      setDraft(null);
      return;
    }
    rename.mutate(
      { occurrenceId: automation.occurrenceId, label },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
      <EventRepeatOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
      {draft === null ? (
        <>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {runLabelOf(automation)}
          </Typography>
          <IconButton
            size="small"
            aria-label="Rename run"
            onClick={() => setDraft(runLabelOf(automation))}
          >
            <EditOutlined sx={{ fontSize: 14 }} />
          </IconButton>
        </>
      ) : (
        <TextField
          size="small"
          autoFocus
          value={draft}
          disabled={rename.isPending}
          error={rename.isError}
          onChange={(event) => setDraft(event.target.value.slice(0, MAX_LABEL))}
          onBlur={handleSave}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSave();
            if (event.key === 'Escape') setDraft(null);
          }}
          slotProps={{ htmlInput: { 'aria-label': 'Run name' } }}
        />
      )}
    </Stack>
  );
}
